import { useState, useRef, useEffect } from 'react';
import { Modal, Box, Button, TextField } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import '../translations.js';
import './addBotModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faSpinner,
  faArrowUpFromBracket,
  faCheckCircle,
  faCircleXmark,
  faAddressCard,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import KnowledgeWarningModal from './knowledgeWarningModal';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';

import { MAX_SOURCE_FILE_BYTES } from '../utils/knowledgeLimits.js';
import { ASK_HOSTING_QUESTION, URL_GATE_DISABLED, isUrlTrainingHidden } from '../utils/webflowGate';
// Per-file cap mirrors the backend's ingest_limits (10 MB, all sources).
const MAX_FILE_SIZE_BYTES = MAX_SOURCE_FILE_BYTES;
const MAX_TOTAL_SIZE_MB = 100;
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

// Must stay identical to DEFAULT_ALLOWED_EXTENSIONS in uploadFileModal.js, which
// mirrors the backend's hard ext_to_mime allow-list (src/controller/knowledge.py).
// Validated by extension, not MIME: browsers report MIME inconsistently per OS
// and file type, so a MIME list rejects files the backend ingests fine.
// No .html on purpose — see the note in uploadFileModal.js.
const ALLOWED_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'rtf', 'txt', 'csv', 'md'];
const ACCEPT_ATTR = '.pdf,.doc,.docx,.rtf,.txt,.csv,.md';

const isAllowedFile = (file) => {
  const parts = (file.name || '').split('.');
  if (parts.length < 2) return false;
  return ALLOWED_FILE_EXTENSIONS.includes((parts.pop() || '').toLowerCase());
};

function isValidUrlSimple(url) {
  if (!url) return false;
  const trimmed = url.trim();
  const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
  return regex.test(trimmed);
}

const AddBotModal = ({ open, onClose, onSubmit }) => {
  // Webflow Marketplace compliance: an account connected to Webflow must not
  // see any arbitrary-URL training input. Read the ACCOUNT-level signal
  // (auth/validate_token -> webflow_connected): a per-bot flag is blind for a
  // freshly connected account whose first bot does not exist yet, or is still
  // building (SelectedBotContext only lists ready bots).
  const { bots } = useSelectedBot();
  const isWebflowUser =
    localStorage.getItem('webflow_connected') === '1' ||
    localStorage.getItem('webflow_history') === '1' ||
    bots.some((b) => b.webflow_connected);
  const [validationError, setValidationError] = useState('');
  const [showValidationError, setShowValidationError] = useState(false);
  const [countdownKey, setCountdownKey] = useState(0);
  const validationTimeoutRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({ bot_name: false, url: false });
  const [formData, setFormData] = useState({
    url: '',
    bot_name: '',
    language: 'en',
    user_id: localStorage.getItem("user_id"),
    send_email_to_user: false,
    javascript_rendering: false
  });
  // Hosting gate: null = question not answered yet (the form stays hidden),
  // true/false = the owner's answer. "true" makes this agent Webflow-hosted,
  // which permanently removes every URL-training surface for it.
  const [webflowHosted, setWebflowHosted] = useState(null);
  // Webflow-managed = the whole account is Webflow-connected, or the owner
  // answered "yes" to the hosting question for this agent. Persisted on
  // submit even while the URL gate is disabled, so the data stays intact.
  const webflowManagedAgent = isWebflowUser || webflowHosted === true;
  // Display only: always false while the gate is disabled (utils/webflowGate.js).
  const hideUrlField = isUrlTrainingHidden(webflowManagedAgent);
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [invalidTypeFiles, setInvalidTypeFiles] = useState([]);
  const [tooLargeFiles, setTooLargeFiles] = useState([]);
  const [exceedsTotalLimitFiles, setExceedsTotalLimitFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [showKnowledgeWarningModal, setShowKnowledgeWarningModal] = useState(false);
  const [knowledgeWarningMessage, setKnowledgeWarningMessage] = useState('');
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (
      invalidTypeFiles.length === 0 &&
      tooLargeFiles.length === 0 &&
      exceedsTotalLimitFiles.length === 0
    ) {
      setFileError('');
    }
  }, [invalidTypeFiles.length, tooLargeFiles.length, exceedsTotalLimitFiles.length]);

  const clearValidationTimer = () => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }
  };

  const showValidationMessage = (message) => {
    if (!message) {
      return;
    }
    setValidationError(message);
    setShowValidationError(true);
    setCountdownKey(prev => prev + 1);
    clearValidationTimer();
    validationTimeoutRef.current = setTimeout(() => {
      setShowValidationError(false);
      setValidationError('');
      validationTimeoutRef.current = null;
    }, 10000);
  };

  const resetFileState = () => {
    setSelectedFiles([]);
    setInvalidTypeFiles([]);
    setTooLargeFiles([]);
    setExceedsTotalLimitFiles([]);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetFormState = () => {
    setFormData({
      url: '',
      bot_name: '',
      language: 'en',
      user_id: localStorage.getItem("user_id"),
      send_email_to_user: false,
      javascript_rendering: false
    });
    setWebflowHosted(null);
    setFieldErrors({ bot_name: false, url: false });
    setValidationError('');
    setShowValidationError(false);
    setShowKnowledgeWarningModal(false);
    setKnowledgeWarningMessage('');
    resetFileState();
    clearValidationTimer();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'bot_name' && value.trim() !== '') {
      setFieldErrors(prev => ({ ...prev, bot_name: false }));
    }

    if (field === 'url') {
      if (value.trim() === '') {
        setFieldErrors(prev => ({ ...prev, url: false }));
      } else if (isValidUrlSimple(value)) {
        setFieldErrors(prev => ({ ...prev, url: false }));
      }
    }
  };

  const uploadFileToAzure = async (file) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}get-azure-sas`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_name: file.name })
        }
      );
      if (!res.ok) throw new Error('Failed to get SAS URL from backend');
      const { sasUrl } = await res.json();
      if (!sasUrl) throw new Error('SAS URL not returned');

      await fetch(sasUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      });

      return sasUrl.split('?')[0];
    } catch (err) {
      console.error('Error uploading to Azure:', err);
      throw err;
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) {
      return;
    }

    const valid = [];
    const invalidType = [];
    const tooLarge = [];
    const exceedTotalLimit = [];

    let currentTotalValidSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);

    for (const file of files) {
      if (!isAllowedFile(file)) {
        invalidType.push(file);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        tooLarge.push(file);
      } else if (currentTotalValidSize + file.size > MAX_TOTAL_SIZE_BYTES) {
        exceedTotalLimit.push(file);
      } else {
        try {
          const azureUrl = await uploadFileToAzure(file);
          valid.push({
            name: file.name,
            size: file.size,
            type: file.type,
            azureUrl
          });
          currentTotalValidSize += file.size;
        } catch {
          invalidType.push(file);
        }
      }
    }

    if (valid.length) {
      setSelectedFiles(prev => [...prev, ...valid]);
    }
    if (invalidType.length) {
      setInvalidTypeFiles(prev => [...prev, ...invalidType]);
    }
    if (tooLarge.length) {
      setTooLargeFiles(prev => [...prev, ...tooLarge]);
    }
    if (exceedTotalLimit.length) {
      setExceedsTotalLimitFiles(prev => [...prev, ...exceedTotalLimit]);
    }

    if (invalidType.length || tooLarge.length || exceedTotalLimit.length) {
      const msgs = [];
      if (invalidType.length) msgs.push(t('addbot_n_invalid_type', { count: invalidType.length }));
      if (tooLarge.length) msgs.push(t('addbot_n_too_large', { count: tooLarge.length }));
      if (exceedTotalLimit.length) msgs.push(t('addbot_n_exceed_total', { count: exceedTotalLimit.length }));
      setFileError(
        `${msgs.join(', ')} ${t('file_will_be_ignored')}`
      );
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles(prevSelected => {
      const updated = [...prevSelected];
      updated.splice(indexToRemove, 1);

      let currentTotalValidSize = updated.reduce((acc, f) => acc + f.size, 0);
      setExceedsTotalLimitFiles(prevExceed => {
        const updatedExceed = [...prevExceed];
        let i = 0;
        while (i < updatedExceed.length) {
          const file = updatedExceed[i];
          if (file.size + currentTotalValidSize <= MAX_TOTAL_SIZE_BYTES) {
            updatedExceed.splice(i, 1);
            currentTotalValidSize += file.size;
          } else {
            i += 1;
          }
        }
        return updatedExceed;
      });

      return updated;
    });
  };

  const submitBot = async ({ bypassKnowledgeWarning = false } = {}) => {
    const nameFilled = formData.bot_name.trim() !== '';
    const urlFilled  = formData.url.trim()      !== '';
    const urlValid   = isValidUrlSimple(formData.url.trim());
    const urlInvalid = urlFilled && !urlValid;

    setFieldErrors({
      bot_name: !nameFilled,
      url: urlInvalid
    });

    const problems = [];
    if (!nameFilled) problems.push(t('name'));
    if (urlInvalid)  problems.push(t('invalid_url'));

    if (problems.length > 0) {
      const message = `${t('missing_details')}: ${problems.join(', ')}`;
      showValidationMessage(message);
      return;
    }

    if (fileError) {
      showValidationMessage(fileError);
      return;
    }

    const hasDocuments = selectedFiles.length > 0;
    const hasKnowledge = urlFilled || hasDocuments;
    if (!hasKnowledge && !bypassKnowledgeWarning) {
      const knowledgeWarning =
        t('no_knowledge_warning') ||
        'No URL or documents provided. The agent will have no knowledge. Do you want to continue?';
      setKnowledgeWarningMessage(knowledgeWarning);
      setShowKnowledgeWarningModal(true);
      return;
    }

    setValidationError('');
    setShowValidationError(false);
    setShowKnowledgeWarningModal(false);
    setKnowledgeWarningMessage('');

    if (urlFilled) {
      const trimmedUrl = formData.url.trim();
      const encodedUrl = encodeURIComponent(trimmedUrl);

      setIsCheckingUrl(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}url/check?url=${encodedUrl}`);
        if (!response.ok) {
          const message = t('invalid_url') || 'API request failed';
          showValidationMessage(message);
          return;
        }

        const result = await response.json();

        if (result.reachable !== true) {
          const message = result.decline_reason || t('invalid_url') || 'URL not reachable';
          showValidationMessage(message);
          return;
        }

        if (result.is_valid !== "yes") {
          const message = result.decline_reason || t('invalid_url') || 'Invalid business URL';
          showValidationMessage(message);
          return;
        }

        if (window.rdt) {
          window.rdt('track', 'Lead');
        }
      } catch (error) {
        console.error("Error checking URL validity:", error);
        const message = t('invalid_url') || 'Unexpected error';
        showValidationMessage(message);
        return;
      } finally {
        setIsCheckingUrl(false);
      }
    }

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });
    // Persist the hosting answer so the agent stays gated everywhere, on every
    // device — a client-only flag would not survive a different browser.
    formDataToSend.append('webflow_hosted', webflowManagedAgent ? 'true' : 'false');
    selectedFiles.forEach(file => {
      if (file.azureUrl) {
        formDataToSend.append('fileUrls', file.azureUrl);
      }
    });

    onSubmit(formDataToSend);
    resetFormState();
    onClose();
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    submitBot();
  };

  const handleConfirmKnowledgeWarning = () => {
    setShowKnowledgeWarningModal(false);
    submitBot({ bypassKnowledgeWarning: true });
  };

  const handleCancelKnowledgeWarning = () => {
    setShowKnowledgeWarningModal(false);
    setKnowledgeWarningMessage('');
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose();
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            borderRadius: '10px',
            boxShadow: 24,
            p: 4,
            padding: isMobile ? '15px' : '20px',
            width: isMobile ? '85%' : '90%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >

        {/* ──────────  HOSTING GATE  ──────────
            Asked before anything else: a Webflow-hosted agent must never be
            offered arbitrary-URL training (Marketplace rules), and the answer
            is stored on the agent so the Knowledge Base stays consistent.
            An account that is already Webflow-connected skips the question.
            Skipped entirely while the gate is disabled or the question is
            switched off (utils/webflowGate.js). */}
        {!URL_GATE_DISABLED && ASK_HOSTING_QUESTION && webflowHosted === null && !isWebflowUser ? (
          <div className='add-bot-section add-bot-hosting-gate'>
            {/* Question centred on the modal width; the X sits on the same
                line, absolutely positioned so it never shifts the title. */}
            <div className='add-bot-hosting-gate-head'>
              <p className='add-bot-hosting-gate-title'>{t('addbot_hosting_question')}</p>
              <button
                type="button"
                className='add-bot-close-btn'
                onClick={handleClose}
                aria-label={t('cancel')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <img
              src="/webflow-logo.png"
              alt="Webflow"
              className='add-bot-hosting-gate-logo'
            />
            <p className='add-bot-hosting-gate-hint'>{t('addbot_hosting_hint')}</p>
            <div className='add-bot-hosting-gate-actions'>
              <Button
                variant="outlined"
                onClick={() => setWebflowHosted(true)}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  borderColor: '#5616ea',
                  color: '#5616ea',
                  '&:hover': { borderColor: '#5616ea', backgroundColor: 'rgba(86,22,234,0.06)' },
                }}
              >
                {t('addbot_hosting_yes')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setWebflowHosted(false)}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  borderColor: '#5616ea',
                  color: '#5616ea',
                  '&:hover': { borderColor: '#5616ea', backgroundColor: 'rgba(86,22,234,0.06)' },
                }}
              >
                {t('addbot_hosting_no')}
              </Button>
            </div>
          </div>
        ) : (
        <>
        {/* Webflow-hosted agents: say why there is no website field. */}
        {hideUrlField && (
          <div className='add-bot-hosting-note'>
            <img src="/webflow-logo.png" alt="Webflow" className='add-bot-hosting-note-logo' />
            <span>{t('addbot_hosting_webflow_note')}</span>
          </div>
        )}

        {/* ----------  NAME  ---------- */}
        <div className='add-bot-section'>
          <div className='icon-title-container'>
            <FontAwesomeIcon className='add-bot-section-icon' icon={faAddressCard} style={{ marginTop: '2.5px' }}/>
            <p className='add-bot-section-title'>{t('addbot_name_your_agent')} <span className='add-bot-required-field'>*</span></p>
          </div>
          <TextField
            fullWidth
            label={t('bot_name')}
            variant="outlined"
            placeholder={t('addbot_name_ph')}
            value={formData.bot_name}
            onChange={(e) => handleInputChange('bot_name', e.target.value)}
            margin="none"
            required
            error={fieldErrors.bot_name}
            disabled={isCheckingUrl}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#5616ea',
                },
              },
              '& .MuiOutlinedInput-root.Mui-error fieldset': {
                borderColor: '#dc0e0e',
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
                '&.Mui-error': {
                  color: '#dc0e0e',
                },
              },
            }}
          />
        </div>

        {/* ----------  URL (hidden for Webflow-hosted agents/accounts)  ---------- */}
        {!hideUrlField && (
        <div className='add-bot-section'>
          <div className='icon-title-container'>
            <FontAwesomeIcon className='add-bot-section-icon' icon={faGlobe}/>
            <p className='add-bot-section-title'>{t('addbot_website_url_optional')}</p>
          </div>
          <TextField
            fullWidth
            label={t('url')}
            variant="outlined"
            value={formData.url}
            onChange={(e) => handleInputChange('url', e.target.value)}
            margin="none"
            placeholder="https://example.com"
            error={fieldErrors.url}
            disabled={isCheckingUrl}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#5616ea',
                },
              },
              '& .MuiOutlinedInput-root.Mui-error fieldset': {
                borderColor: '#dc0e0e',
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
                '&.Mui-error': {
                  color: '#dc0e0e',
                },
              },
            }}
          />
        </div>
        )}

        {/* ----------  DOCUMENTS  ---------- */}
        <div className='add-bot-section'>
          <div className='icon-title-container'>
            <FontAwesomeIcon className='add-bot-section-icon' icon={faArrowUpFromBracket} />
            <p className='add-bot-section-title'>{t('addbot_upload_documents_optional')}</p>
          </div>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => e.preventDefault()}
            className="file-upload-container"
            style={{
              border: '2px dashed #ccc',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              background: '#fafafa'
            }}
          >
            <div className="file-drag-drop">
              <FontAwesomeIcon icon={faArrowUpFromBracket} />
              {t('drag_and_drop_here')}
            </div>

            <div className="add-bot-file-tag-parent">
              {ALLOWED_FILE_EXTENSIONS.map((ext) => (
                <div className="add-bot-file-tag" key={ext}>.{ext}</div>
              ))}
            </div>

            <button
              className="upload-files-button"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              {t('select_files')}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {(selectedFiles.length > 0 ||
              invalidTypeFiles.length > 0 ||
              tooLargeFiles.length > 0 ||
              exceedsTotalLimitFiles.length > 0) && (
              <div className="file-parent-container">
                {selectedFiles.length > 0 && (
                  <div className="files-container">
                    {selectedFiles.map((file, idx) => (
                      <div key={file.name + idx} className="valid-file">
                        <div className="file-content">
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            style={{ color: '#5616ea', fontSize: 14, marginRight: 8 }}
                          />
                          <span>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFile(idx)}
                          style={{ minWidth: '30px', fontWeight: 600 }}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {invalidTypeFiles.length > 0 && (
                  <>
                    <div className="invalid-header-text">
                      {t('invalid_file_type')}
                    </div>
                    <div className="files-container">
                      {invalidTypeFiles.map((file, idx) => (
                        <div key={file.name + idx} className="invalid-file">
                          <div className="file-content">
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
                            />
                            <span>{file.name}</span>
                          </div>
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              setInvalidTypeFiles(prev => prev.filter((_, i) => i !== idx))
                            }
                            style={{ minWidth: '30px', fontWeight: 600 }}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {tooLargeFiles.length > 0 && (
                  <>
                    <div className="invalid-header-text">
                      {t('file_too_large')}
                    </div>
                    <div className="files-container">
                      {tooLargeFiles.map((file, idx) => (
                        <div key={file.name + idx} className="invalid-file">
                          <div className="file-content">
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
                            />
                            <span>
                              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              setTooLargeFiles(prev => prev.filter((_, i) => i !== idx))
                            }
                            style={{ minWidth: '30px', fontWeight: 600 }}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {exceedsTotalLimitFiles.length > 0 && (
                  <>
                    <div className="invalid-header-text">
                      {t('exceeds_total_limit')}
                    </div>
                    <div className="files-container">
                      {exceedsTotalLimitFiles.map((file, idx) => (
                        <div key={file.name + idx} className="invalid-file">
                          <div className="file-content">
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
                            />
                            <span>
                              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              setExceedsTotalLimitFiles(prev => prev.filter((_, i) => i !== idx))
                            }
                            style={{ minWidth: '30px', fontWeight: 600 }}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {fileError && (
              <div className="invalid-header-text" style={{ marginTop: '10px', color: '#dc0e0e' }}>
                {fileError}
              </div>
            )}
          </div>
        </div>

        {/* ----------  VALIDATION MESSAGE  ---------- */}
        {validationError && showValidationError && (
          <div className="validation-error-container">
            <svg key={countdownKey} className="countdown-ring" width="24" height="24">
              <circle
                className="ring-bg"
                cx="12"
                cy="12"
                r="10"
                fill="transparent"
                stroke="#dc0e0e"
                strokeWidth="2"
              />
            </svg>
            <span>
              {validationError}
            </span>
          </div>
        )}

        {/* ----------  ACTION BUTTONS  ---------- */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isCheckingUrl}
            sx={{
                borderColor: '#5616ea',
                color: '#5616ea',
                '&:hover': {
                  borderColor: '#4a14d6',
                  backgroundColor: "#f0f0f0"
                },
              }}>
            {t('cancel')}
          </Button>
          <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isCheckingUrl}
              sx={{
                backgroundColor: '#5616ea',
                '&:hover': {
                  backgroundColor: '#4a14d6',
                },
              }}
            >
            {isCheckingUrl ? (
              <FontAwesomeIcon icon={faSpinner} spin className="btn-spinner" />
            ) : (
              t('create_bot')
            )}
          </Button>
        </div>
        </>
        )}
        </Box>
      </Modal>
      <KnowledgeWarningModal
        open={showKnowledgeWarningModal}
        onConfirm={handleConfirmKnowledgeWarning}
        onCancel={handleCancelKnowledgeWarning}
        message={knowledgeWarningMessage}
      />
    </>
  );
};

export default AddBotModal;
